import moment from "moment/moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import actions from "@/actions";
import InformationEditorContainer from "@/containers/home/InformationEditorContainer";
import InformationViewerContainer from "@/containers/home/InformationViewerContainer";
import { useDebounceLoading } from "@/hooks/useDebounceLoading";
import { isValid } from "@/lib/roleChecker";
import { RootState } from "@/reducers/types";

export const InformationBoard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { roles } = useSelector((state: RootState) => state.app);
  const { items: informations, current: currentInformation } = useSelector((state: RootState) => state.information);

  const [fetching, setFetching] = useState(false);
  const { loading } = useDebounceLoading(fetching);
  const [showInformationViewer, setShowInformationViewer] = useState(false);
  const [fetchingInformation, setFetchingInformation] = useState(false);
  const [showInformationEditor, setShowInformationEditor] = useState(false);
  const [information, setInformation] = useState(null);

  useEffect(() => {
    handleSearch();
  }, []);

  const handleSearch = async () => {
    setFetching(true);

    dispatch(
      actions.information.fetchItems({ start: 1, limit: 999999 }, () => {
        setFetching(false);
      })
    );
  };

  const openInformationEditor = (informationId) => {
    if (informationId > 0) {
      setFetchingInformation(true);

      dispatch(
        actions.information.fetchItem(informationId, (data) => {
          setFetchingInformation(false);
          setShowInformationEditor(true);
          setInformation(data);
        })
      );
    } else {
      setShowInformationEditor(true);
      setInformation(null);
      setFetchingInformation(false);
    }
  };

  const hideInformationEditor = () => {
    setShowInformationEditor(false);
    setInformation(null);
  };

  const openInformationViewer = (informationId) => {
    setShowInformationViewer(true);
    setFetchingInformation(true);

    dispatch(actions.information.fetchItem(informationId, () => setFetchingInformation(false)));
  };

  const hideInformationViewer = () => {
    setShowInformationViewer(false);
  };

  return (
    <>
      <h2 className="page-ttl">
        {t("board")}
        {isValid(roles, "information", "create") && (
          <span
            data-test-id="button-home-create_information"
            className="ml-[15px] text-[#0866c6] hover:cursor-pointer hover:text-[#0753a1]"
            onClick={() => openInformationEditor(0)}
          >
            <img src="./img/icon-add-blue.svg" className="h-[25px]" />
          </span>
        )}
      </h2>
      {fetching || informations.length ? (
        <div className={`tbl-box h-[calc(100%-55px)] ${loading ? "loading loading--list" : ""}`}>
          <div className="tbl-head-adjusted">
            <table className="tbl-basic tbl-data">
              <thead>
                <tr>
                  <th className="w-full">{t("board_subject_full")}</th>
                  <th className="min-w-[110px] max-w-[110px]">{t("created_by")}</th>
                  <th className="min-w-[180px] max-w-[180px]">{t("created_at")}</th>
                  <th className="min-w-[70px] max-w-[170px]">{t("edit")}</th>
                </tr>
              </thead>
            </table>
          </div>
          <div className="overflow-y-scroll h-[calc(100%-28px)]">
            <table className="tbl-basic tbl-data table-fixed">
              <tbody>
                {informations.map((information, index) => (
                  <tr key={index}>
                    <td className="w-full">
                      <a onClick={() => openInformationViewer(information.information_id)}>{information.subject}</a>
                      {information.file_status && (
                        <span className="icon">
                          <img className="w-[16px] h-[16px] float-right" src="./img/icon-clip-black.svg" />
                        </span>
                      )}
                    </td>
                    <td className="w-110 txt-center">{information.timestamp.insert_user_name}</td>
                    <td className="w-180 txt-center">
                      {moment(information.timestamp.insert_date).format("YYYY/MM/DD HH:mm")}
                    </td>
                    <td className="w-70 txt-center">
                      {isValid(information.roles, "__self__", "update") && (
                        <a onClick={() => openInformationEditor(information.information_id)}>{t("edit")}</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="empty-message">{t("no_data")}</p>
      )}
      {showInformationEditor && (
        <InformationEditorContainer
          data={information}
          fetching={fetchingInformation}
          closeHandler={() => hideInformationEditor()}
        />
      )}
      {showInformationViewer && (
        <InformationViewerContainer
          data={currentInformation}
          fetching={fetchingInformation}
          closeHandler={() => hideInformationViewer()}
        />
      )}
    </>
  );
};
